/**
 * The key this device can open a learner's reading place with.
 *
 * The key itself is derived from the password at sign-in and of that password
 * itself nothing is kept (see `#/lib/flashbookVault` and the session's own login).
 * What is kept is the *derived* key, per account, because the alternative is to ask
 * a learner for their password again every time the app starts — and because the
 * place has to be writable the moment a book is opened, which is long after the
 * password was in anybody's hands.
 *
 * Two decisions, and both are about what happens when the key is *not* there.
 *
 *  - **Per account, not per device.** Two accounts on one phone are two learners,
 *    and a single slot would have the second one's sign-in lock the first one out
 *    of their own book. Switching accounts does not ask for a password (see
 *    `resumeSession`), so the key of the account being switched *to* has to still be
 *    here — which is exactly what the wrong shape of this store would lose.
 *
 *  - **Signing out removes it.** A key that outlives the session is a key that can
 *    read records in a repo the learner has left a device signed out of, and signing
 *    out has to mean what it says. Nothing is lost by removing it: signing back in
 *    derives the same key from the same password, and a *different* password is the
 *    one case where an old key should not be around anyway.
 *
 * It is a device store rather than a keychain, and that is worth stating plainly:
 * the exposure this closes is the learner's own repo being *public*, and what it
 * leaves is a key in the app's own storage on a device the learner is holding.
 * Moving the key into the platform's keychain is a change to this file alone.
 */
import {vaultKeyFromBase64, vaultKeyToBase64} from '#/lib/flashbookVault'
import {device} from '#/storage'

/**
 * The key for one account, or `null` when this device has never derived one.
 *
 * `null` is a normal state rather than a failure: it is what a device that has not
 * been signed in on since this landed looks like, and what a signed-out device
 * looks like. Every caller turns it into "keep the place here, write nothing to the
 * repo", which is the behaviour the feature already has for a learner with no
 * account at all.
 */
export function readFlashBookVaultKey(did: string | null | undefined) {
  if (!did) return null
  return vaultKeyFromBase64(device.get(['flashbookVaultKeys'])?.[did])
}

/** Keep one account's key, beside whatever is held for the others. */
export function putFlashBookVaultKey(did: string, key: Uint8Array): void {
  device.set(['flashbookVaultKeys'], {
    ...device.get(['flashbookVaultKeys']),
    [did]: vaultKeyToBase64(key),
  })
}

/**
 * Forget one account's key.
 *
 * A no-op when there is nothing held — which is the common case, because signing
 * out happens on devices signed in before this existed too.
 */
export function clearFlashBookVaultKey(did: string): void {
  const held = device.get(['flashbookVaultKeys'])
  if (!held || !(did in held)) return
  const next = {...held}
  delete next[did]
  device.set(['flashbookVaultKeys'], next)
}
